import { Button } from "@devhop/ui";
import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeft,
  ClipboardCheck,
  FileDown,
  HelpCircle,
  Pencil,
} from "lucide-react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { usePermissions } from "@/modules/auth/presentation/model/usePermissions";
import { Permissions } from "@/modules/roles/domain/contracts/permissions";
import { formatDateLocal } from "@/shared/lib/date-time";
import { useMeetingByIdQuery } from "../api/queries";
import { MeetingTour, meetingDetailTourSteps, useMeetingTour } from "../tour";
import { MeetingForm } from "../ui/MeetingForm";

export function MeetingDetailPage() {
  const params = useParams({ strict: false });
  const id = params?.id as string | undefined;
  const nav = useNavigate();
  const detailQuery = useMeetingByIdQuery(id);
  const meeting = detailQuery.data;
  const canUpdate = usePermissions().has(Permissions.mgmt_meeting.update);
  const tour = useMeetingTour("detail");

  const handleBack = () =>
    nav({
      to: "/app/management/meetings",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        dateFrom: undefined,
        dateTo: undefined,
        search: undefined,
      },
    });

  const handleEdit = () => {
    if (!id) return;
    nav({ to: "/app/management/meetings/$id/edit", params: { id } });
  };

  const handlePdfPreview = () => {
    if (!id) return;
    nav({ to: "/app/management/meetings/$id/pdf-preview", params: { id } });
  };

  const handleViewActions = () => {
    if (!id) return;
    nav({
      to: "/app/management/meetings/actions",
      search: {
        offset: 0,
        limit: 20,
        sort: undefined,
        dueDateFrom: undefined,
        dueDateTo: undefined,
        status: undefined,
        assigneeId: undefined,
        meetingId: id,
      },
    });
  };

  if (detailQuery.isLoading || !id) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-3xl py-8 text-muted-foreground">
            {detailQuery.isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບບັນທຶກການປະຊຸມ"}
          </div>
        </Main>
      </>
    );
  }

  if (!meeting) {
    return (
      <>
        <Header />
        <Main>
          <div className="mx-auto max-w-3xl py-8 text-muted-foreground">
            ບໍ່ພົບບັນທຶກການປະຊຸມ
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header />
      <Main>
        <MeetingTour
          steps={meetingDetailTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
        <div
          className="mb-6 flex flex-wrap items-center justify-between gap-4"
          data-tourid="meeting-detail-header"
        >
          <div className="flex items-center gap-2">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="font-semibold text-2xl">{meeting.title}</h1>
                <Button
                  variant="ghost"
                  size="icon"
                  className="size-7 shrink-0 rounded-full"
                  onClick={tour.startTour}
                  title="ເລີ່ມທົດສອບການນຳທາງ"
                  aria-label="ເລີ່ມທົດສອບການນຳທາງ"
                >
                  <HelpCircle className="size-5" />
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                {formatDateLocal(meeting.meetingDate)}
                {meeting.createdByName && ` · ຜູ້ສ້າງ: ${meeting.createdByName}`}
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={handleBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              ກັບຄືນ
            </Button>
            <Button variant="outline" onClick={handlePdfPreview}>
              <FileDown className="mr-2 h-4 w-4" />
              ເບິ່ງ PDF / ດາວໂຫຼດ
            </Button>
            <Button variant="outline" onClick={handleViewActions}>
              <ClipboardCheck className="mr-2 h-4 w-4" />
              ປະເດັນ
            </Button>
            {canUpdate && (
              <Button onClick={handleEdit}>
                <Pencil className="mr-2 h-4 w-4" />
                ແກ້ໄຂ
              </Button>
            )}
          </div>
        </div>
        <div
          className="mx-auto max-w-3xl space-y-6"
          data-tourid="meeting-detail-content"
        >
          <MeetingForm mode="detail" meeting={meeting} onCancel={handleBack} />
        </div>
      </Main>
    </>
  );
}
